import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import TicketQR from '../components/TicketQR';
import RoulettePopup from '../components/RoulettePopup';
import { money, fmtDate, fullName, accesoLabel, tipoLote } from '../events/util';
import FiltroTipoLote, { filtrarPorTipo, tipoInicial } from '../components/FiltroTipoLote';
import { variablesTema, cargarFuente } from '../tema';
import { diaYMes } from './CumpleForm';

// Nombre visible de cada tipo en la referencia de "Todos".
const TIPO_LABEL = { ingreso: 'Ingreso', combo: 'Combo', traslado: 'Traslado' };
import { usePresale, fmtPresale } from '../hooks/usePresale';
import { venueUrl } from '../tenant';

const API = process.env.REACT_APP_API_URL || '';
const STEPS = ['Entrada', 'Datos', 'Pago', 'Listo'];

export default function EventPublic() {
  const { eventId } = useParams();
  const [params] = useSearchParams();
  const ref = params.get('ref');

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState(ref ? 3 : 0);

  // selección
  const [lotId, setLotId] = useState('');
  // null = todavía no eligió: se usa el tipo inicial que corresponda al evento. No se
  // puede inicializar con el valor real porque los lotes llegan después del fetch.
  const [tipo, setTipo] = useState(null);
  const [mesaId, setMesaId] = useState('');
  const [qty, setQty] = useState(1);
  const [coupon, setCoupon] = useState('');
  const [couponInfo, setCouponInfo] = useState(null);
  const [buyer, setBuyer] = useState({ name: '', lastname: '', email: '', birthdate: '', dni: '', phone: '', city: '', marketing_consent: false });
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/public/event/${eventId}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => { setData(d); cargarFuente(d && d.event && d.event.tipografia); })
      .catch(() => setError('No se encontró el evento.'));
  }, [eventId]);

  // LA PROMO LA CALCULA EL SERVIDOR, con la misma función que usa al armar la orden. La
  // pantalla sumaba precio × cantidad y nada más: con un 4x3 mostraba $44.000 y Mercado
  // Pago cobraba $33.000. Va antes de los return tempranos: es un hook.
  const [cot, setCot] = useState(null);
  useEffect(() => {
    if (!lotId || mesaId) { setCot(null); return; }
    let vivo = true;
    const t = setTimeout(() => {
      fetch(`${API}/api/public/event/${eventId}/cotizar?lot_id=${lotId}&qty=${qty}`)
        .then(r => r.ok ? r.json() : null)
        .then(c => { if (vivo) setCot(c); })
        .catch(() => { if (vivo) setCot(null); });
    }, 200);
    return () => { vivo = false; clearTimeout(t); };
  }, [eventId, lotId, qty, mesaId]);

  const presale = usePresale(data?.event?.sales_start_at);

  if (error) return <Shell><div style={{ color: 'var(--red-300)', textAlign: 'center' }}>{error}</div></Shell>;
  if (!data) return <Shell><div style={{ color: 'var(--ink-600)', textAlign: 'center' }}>Cargando…</div></Shell>;

  const { event, lots } = data;
  const mesas = data.mesas || [];
  const cumple = data.cumple || null;
  const tipoSel = tipo ?? tipoInicial(lots);
  const lotesVisibles = filtrarPorTipo(lots, tipoSel);
  // Qué es cada cosa, en la línea de abajo del filtro. Va acá y no en el nombre del lote
  // porque el nombre lo escribe cada boliche: "COMBO ROLDAN" no le dice a nadie de otra
  // ciudad si incluye la entrada o es solo el micro. La de traslado aclara lo que NO
  // incluye: alguien puede comprar solo el micro creyendo que entra con eso.
  const AYUDA_TIPO = {
    ingreso: 'Solo el ingreso al evento.',
    combo: 'Incluye el ingreso al evento y el traslado.',
    traslado: 'Solo el traslado. No incluye el ingreso al evento.',
  };
  const lot = lots.find(l => l.id === lotId);
  const mesa = mesas.find(m => m.id === mesaId);
  const isMesa = !!mesaId;
  const setB = (k, v) => setBuyer(s => ({ ...s, [k]: v }));

  async function validateCoupon() {
    if (!coupon.trim()) { setCouponInfo(null); return; }
    try {
      const qs = `event_id=${eventId}&lot_id=${lotId || ''}`;
      const r = await fetch(`${API}/api/public/coupon/${coupon.trim().toUpperCase()}?${qs}`);
      const d = await r.json();
      setCouponInfo(r.ok ? d : { valid: false, error: d.error });
    } catch { setCouponInfo({ valid: false, error: 'Error' }); }
  }

  // Mesa: precio fijo (una unidad), sin cupón ni cantidad. Lote: como siempre.
  const subtotal = isMesa ? Number(mesa?.price || 0) : (lot ? Number(lot.price) * qty : 0);
  // El cupón descuenta sobre UNA entrada, no sobre toda la compra (mismo cálculo que
  // hace el servidor al cobrar, para que lo que se muestra sea lo que se paga).
  const unitPrice = lot ? Number(lot.price) : 0;
  const discount = isMesa || !couponInfo?.valid ? 0
    : (couponInfo.type === 'fixed'
        ? Math.min(unitPrice, Number(couponInfo.amount) || 0)
        : Math.round(unitPrice * (Number(couponInfo.percent) || 0) / 100));
  // La promo, solo si la cotización es de ESTA cantidad y este lote: mientras llega la
  // nueva no se muestra el descuento de la anterior. Se suma al cupón, igual que al cobrar.
  const promoMonto = !isMesa && cot && cot.qty === qty && cot.promo ? Number(cot.promo.monto) : 0;
  const net = Math.max(0, subtotal - discount - promoMonto);
  const feePct = Number(event.service_fee_pct) || 0;
  const serviceFee = isMesa ? 0 : Math.round(net * feePct / 100);
  const total = isMesa ? Number(mesa?.price || 0) : net + serviceFee;

  async function pay() {
    setPaying(true);
    try {
      const body = isMesa
        ? { event_id: eventId, mesa_id: mesaId, buyer }
        : { event_id: eventId, lot_id: lotId, quantity: qty, buyer, coupon_code: couponInfo?.valid ? coupon.trim().toUpperCase() : null };
      const r = await fetch(`${API}/api/public/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) { alert(d.error || 'No se pudo iniciar el pago'); return; }
      window.location.href = d.init_point;
    } catch { alert('Error de conexión'); } finally { setPaying(false); }
  }

  return (
    <Shell logo={event.logo_url} slug={event.slug} tema={variablesTema(event)}>
      <RoulettePopup eventId={eventId} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 36, alignItems: 'start' }} className="ev-pub-grid">
        {/* IZQUIERDA */}
        <div>
          <div style={{ color: 'var(--red-400)', fontSize: 13, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{fmtDate(event.date)}</div>
          <div className="lb-display" style={{ fontSize: 40, marginTop: 8 }}>{event.name}</div>
          <div style={{ color: 'var(--ink-700)', fontSize: 14, marginTop: 8 }}>
            {[event.start_time, event.end_time].filter(Boolean).join(' — ')}{event.venue ? ` · ${event.venue}` : ''}
          </div>
          <Countdown date={event.date} time={event.start_time} />
          {Array.isArray(event.lineup) && event.lineup.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Line up</div>
              {event.lineup.map((row, i) => {
                const [name, role, time] = Array.isArray(row) ? row : [row.name, row.role, row.time];
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <div><span style={{ color: '#fff', fontWeight: 600 }}>{name}</span> <span style={{ color: 'var(--ink-600)', fontSize: 12 }}>· {role}</span></div>
                    <span style={{ color: 'var(--ink-700)', fontSize: 13 }}>{time}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* BENEFICIO DE CUMPLEAÑOS. Va debajo de los datos del evento, y el botón lleva a
              la página del formulario (no abre una ventanita) para que esa página tenga su
              propio link y se pueda pegar en una historia. */}
          {cumple && (
            <div style={{ marginTop: 28, padding: '18px 20px', borderRadius: 6, border: '1px solid var(--red-500)', background: 'linear-gradient(180deg, rgba(139,15,26,0.18), rgba(139,15,26,0.04))' }}>
              <div className="lb-eyebrow" style={{ color: 'var(--red-400)' }}>
                Beneficio cumpleañeros · Del {diaYMes(cumple.desde)} al {diaYMes(cumple.hasta)}
              </div>
              {cumple.beneficio && <div style={{ color: '#fff', fontSize: 14.5, marginTop: 8, lineHeight: 1.5 }}>{cumple.beneficio}</div>}
              <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 6, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Exclusivo mayores de 18 años</div>
              {/* El bloque sigue visible con la lista llena: que desaparezca sin
                  explicación hace que pregunten por mensaje privado. */}
              {cumple.lleno
                ? <div style={{ fontSize: 12.5, color: 'var(--ink-700)', marginTop: 12 }}>La lista ya está completa. El beneficio es sólo para quienes están anotados.</div>
                : cumple.abierto
                  ? <a href={`/e/${eventId}/cumple`} className="lb-btn lb-btn--primary lb-btn--sm" style={{ marginTop: 14, display: 'inline-block' }}>Anotarme en la lista</a>
                  : <div style={{ fontSize: 12.5, color: 'var(--ink-700)', marginTop: 12 }}>La lista ya está cerrada. El beneficio es sólo para quienes están anotados.</div>}
            </div>
          )}
        </div>

        {/* DERECHA */}
        <div className="lb-card" style={{ padding: '26px 26px' }}>
          {presale.active ? <PresaleBlock p={presale} /> : (<>
          {/* stepper */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
            {STEPS.map((s, i) => (
              <div key={s} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ height: 3, background: i <= step ? 'var(--red-500)' : 'var(--ink-400)', borderRadius: 2 }} />
                <div style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: i <= step ? '#fff' : 'var(--ink-600)', marginTop: 6 }}>{s}</div>
              </div>
            ))}
          </div>

          {step === 0 && (
            <div className="lb-fade">
              <div className="lb-h" style={{ fontSize: 18, marginBottom: 16 }}>Elegí tu acceso</div>
              {/* Con ingreso, VIP, 6 combos y 6 traslados sueltos son 15 opciones: elegir
                  es buscar en una lista. El filtro la parte en tres. Solo se dibuja si el
                  evento tiene más de un tipo. */}
              <FiltroTipoLote lotes={lots} valor={tipoSel} onChange={(t) => { setTipo(t); setLotId(''); }} />
              {/* En "Todos" la lista viene mezclada, así que ahí va la referencia de los
                  tres — pero solo de los tipos que este evento realmente tiene. */}
              <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: -2, marginBottom: 12, lineHeight: 1.5 }}>
                {AYUDA_TIPO[tipoSel] || Object.entries(AYUDA_TIPO)
                  .filter(([k]) => lots.some(l => tipoLote(l) === k))
                  .map(([k, txt]) => <div key={k}><b style={{ color: 'var(--ink-800)' }}>{TIPO_LABEL[k]}:</b> {txt}</div>)}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[...lotesVisibles].sort((a, b) => {
                  const soldA = (a.sold_out ?? (a.status === 'sold_out' || (a.max_capacity > 0 && a.sold_count >= a.max_capacity))) ? 0 : 1;
                  const soldB = (b.sold_out ?? (b.status === 'sold_out' || (b.max_capacity > 0 && b.sold_count >= b.max_capacity))) ? 0 : 1;
                  return soldA - soldB; // agotados primero
                }).map(l => {
                  // `sold_out` lo manda el servidor: si el boliche oculta cuántas quedan,
                  // capacidad y vendidas llegan en null y con ellas no se puede calcular.
                  const soldOut = l.sold_out ?? (l.status === 'sold_out' || (l.max_capacity > 0 && l.sold_count >= l.max_capacity));
                  const sel = lotId === l.id;
                  return (
                    <button key={l.id} disabled={soldOut} onClick={() => { setLotId(l.id); setMesaId(''); }} style={{ display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', padding: '14px 16px', borderRadius: 4, cursor: soldOut ? 'not-allowed' : 'pointer', background: sel ? 'rgba(139,15,26,0.12)' : 'var(--ink-050)', border: '1px solid ' + (sel ? 'var(--red-500)' : 'var(--border)'), opacity: soldOut ? 0.5 : 1 }}>
                      <span style={{ width: 16, height: 16, borderRadius: 999, border: '1px solid ' + (sel ? 'var(--red-500)' : 'var(--ink-500)'), display: 'grid', placeItems: 'center', flex: 'none' }}>{sel && <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--red-500)' }} />}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#fff', fontWeight: 600, fontFamily: 'var(--font-display)', fontSize: 14, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                          {l.name}
                          {/* La promo va acá, junto al lote: si solo apareciera en el
                              total, nadie subiría la cantidad para aprovecharla. */}
                          {!soldOut && l.promo && (
                            <span style={{ fontSize: 10, letterSpacing: '0.08em', padding: '2px 7px', borderRadius: 999, background: 'var(--red-600)', color: '#fff', fontFamily: 'var(--font-body)', fontWeight: 700 }}>
                              {l.promo.tipo === 'percent' ? `${l.promo.valor}% OFF` : l.promo.tipo}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>{soldOut ? 'Agotado' : (l.max_capacity > 0 ? `${l.max_capacity - l.sold_count} disponibles` : 'Disponible')}</div>
                        {!soldOut && l.promo?.resumen && (
                          <div style={{ fontSize: 10.5, color: 'var(--red-400)', marginTop: 2 }}>{l.promo.resumen}</div>
                        )}
                      </div>
                      {soldOut ? <span className="lb-status lb-status--used">Agotado</span> : <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: '#fff' }}>{money(l.price)}</span>}
                    </button>
                  );
                })}
                {lots.length === 0 && mesas.length === 0 && <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>No hay entradas online disponibles.</div>}
              </div>

              {mesas.length > 0 && (
                <div style={{ marginTop: 22 }}>
                  <div className="lb-h" style={{ fontSize: 18, marginBottom: 12 }}>Mesas</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {mesas.map(m => {
                      const sel = mesaId === m.id;
                      return (
                        <button key={m.id} onClick={() => { setMesaId(m.id); setLotId(''); setCoupon(''); setCouponInfo(null); }} style={{ display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', padding: '14px 16px', borderRadius: 4, cursor: 'pointer', background: sel ? 'rgba(139,15,26,0.12)' : 'var(--ink-050)', border: '1px solid ' + (sel ? 'var(--red-500)' : 'var(--border)') }}>
                          <span style={{ width: 16, height: 16, borderRadius: 999, border: '1px solid ' + (sel ? 'var(--red-500)' : 'var(--ink-500)'), display: 'grid', placeItems: 'center', flex: 'none' }}>{sel && <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--red-500)' }} />}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#fff', fontWeight: 600, fontFamily: 'var(--font-display)', fontSize: 14, textTransform: 'uppercase' }}>{m.sector_name ? `${m.sector_name} · ` : ''}{m.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>{m.capacity} personas</div>
                          </div>
                          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: '#fff' }}>{money(m.price)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {!isMesa && (
                <div style={{ marginTop: 16 }}>
                  <div className="lb-label" style={{ marginBottom: 6 }}>Cupón (opcional)</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="lb-input" value={coupon} onChange={e => setCoupon(e.target.value.toUpperCase())} placeholder="Código de cupón" />
                    <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={validateCoupon}>Aplicar</button>
                  </div>
                  {couponInfo && (couponInfo.valid
                    ? <div style={{ fontSize: 12, color: '#2ecc71', marginTop: 6 }}>✓ {couponInfo.type === 'fixed' ? `${money(couponInfo.amount)} de descuento` : `${couponInfo.percent}% de descuento`} en 1 entrada</div>
                    : <div style={{ fontSize: 12, color: 'var(--red-300)', marginTop: 6 }}>{couponInfo.error}</div>)}
                </div>
              )}

              {!isMesa && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 18 }}>
                  <span className="lb-label">Cantidad</span>
                  <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid var(--border)', borderRadius: 999 }}>
                    <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ border: 0 }} onClick={() => setQty(q => Math.max(1, q - 1))}>–</button>
                    <span style={{ minWidth: 44, textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18 }}>{qty}</span>
                    <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ border: 0 }} onClick={() => setQty(q => q + 1)}>+</button>
                  </div>
                </div>
              )}

              {/* EL TOTAL, AL ELEGIR LA CANTIDAD. Antes recién aparecía en el paso de pago,
                  y sin la promo. Mostrarlo acá es lo que hace que alguien suba a 4 para
                  aprovechar un 4x3: una promo que no se ve en el total no la usa nadie. */}
              {!isMesa && lot && (
                <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 4, background: 'var(--ink-050)', border: '1px solid ' + (promoMonto > 0 ? 'rgba(46,204,113,0.45)' : 'var(--border)') }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span className="lb-label">Total</span>
                    <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      {(promoMonto > 0 || discount > 0) && <span style={{ fontSize: 13, color: 'var(--ink-600)', textDecoration: 'line-through' }}>{money(subtotal)}</span>}
                      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20, color: '#fff' }}>{money(net)}</span>
                    </span>
                  </div>
                  {promoMonto > 0 && (
                    <div style={{ fontSize: 12, color: '#2ecc71', marginTop: 6 }}>✓ Promoción aplicada · {cot.promo.detalle}</div>
                  )}
                  {serviceFee > 0 && (
                    <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 4 }}>+ cargo por servicio ({feePct}%) en el pago</div>
                  )}
                </div>
              )}

              <button className="lb-btn lb-btn--primary lb-btn--block" style={{ marginTop: 20 }} disabled={!lotId && !mesaId} onClick={() => setStep(1)}>Continuar →</button>
            </div>
          )}

          {step === 1 && (
            <div className="lb-fade">
              <div className="lb-h" style={{ fontSize: 18, marginBottom: 16 }}>Tus datos</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <F label="Nombre"><input className="lb-input" value={buyer.name} onChange={e => setB('name', e.target.value)} /></F>
                <F label="Apellido"><input className="lb-input" value={buyer.lastname} onChange={e => setB('lastname', e.target.value)} /></F>
              </div>
              <div style={{ marginTop: 12 }}>
                <F label="Email" hint="Te enviamos la entrada acá"><input className="lb-input" type="email" value={buyer.email} onChange={e => setB('email', e.target.value)} /></F>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                <F label="Fecha de nacimiento"><input className="lb-input" type="date" value={buyer.birthdate} onChange={e => setB('birthdate', e.target.value)} /></F>
                <F label="DNI"><input className="lb-input" inputMode="numeric" value={buyer.dni} onChange={e => setB('dni', e.target.value.replace(/\D/g, ''))} /></F>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                <F label="Teléfono"><input className="lb-input" inputMode="tel" value={buyer.phone} onChange={e => setB('phone', e.target.value)} /></F>
                <F label="Localidad"><input className="lb-input" value={buyer.city} onChange={e => setB('city', e.target.value)} /></F>
              </div>
              <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, color: 'var(--ink-700)', lineHeight: 1.45, cursor: 'pointer', marginTop: 16 }}>
                <input type="checkbox" checked={buyer.marketing_consent} onChange={e => setB('marketing_consent', e.target.checked)} style={{ marginTop: 2 }} />
                <span>Quiero recibir novedades y promociones de <b style={{ color: '#fff' }}>{data.event?.org_name || 'este boliche'}</b> por email. <span style={{ color: 'var(--ink-600)' }}>(Podés darte de baja cuando quieras.)</span></span>
              </label>
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button className="lb-btn lb-btn--ghost" style={{ flex: 1 }} onClick={() => setStep(0)}>← Volver</button>
                <button className="lb-btn lb-btn--primary" style={{ flex: 2 }} disabled={!buyer.name || !buyer.lastname || !buyer.email} onClick={() => setStep(2)}>Ir a pagar →</button>
              </div>
            </div>
          )}

          {step === 2 && !ref && (
            <div className="lb-fade">
              <div className="lb-h" style={{ fontSize: 18, marginBottom: 16 }}>Pago seguro</div>
              <div className="lb-card" style={{ padding: '18px 20px', background: 'var(--ink-050)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#fff', fontWeight: 600 }}>{isMesa ? `Mesa ${mesa?.name} · ${mesa?.capacity} pers` : `${lot?.name} × ${qty}`}</span>
                  <span style={{ color: '#fff' }}>{money(subtotal)}</span>
                </div>
                {discount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, color: 'var(--red-300)' }}>
                    {/* Se aclara "1 entrada" cuando lleva más de una, para que no
                        parezca un error que el descuento no crezca con la cantidad. */}
                    <span>Cupón{couponInfo?.type === 'percent' ? ` ${couponInfo.percent}%` : ''}{qty > 1 ? ' · 1 entrada' : ''}</span>
                    <span>− {money(discount)}</span>
                  </div>
                )}
                {promoMonto > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, color: '#2ecc71' }}>
                    <span>Promoción aplicada · {cot.promo.detalle}</span>
                    <span>− {money(promoMonto)}</span>
                  </div>
                )}
                {serviceFee > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, color: 'var(--ink-700)' }}><span>Cargo por servicio ({feePct}%)</span><span>+ {money(serviceFee)}</span></div>}
                <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 8 }}>{buyer.name} {buyer.lastname}{buyer.dni ? ` · DNI ${buyer.dni}` : ''}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <span className="lb-eyebrow">Total</span>
                  <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 24, color: '#fff' }}>{money(total)}</span>
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 14 }}>Te redirige a Mercado Pago. Tu entrada se emite al confirmar.</div>
              <button className="lb-btn lb-btn--white lb-btn--block" style={{ marginTop: 16 }} disabled={paying} onClick={pay}>{paying ? 'Redirigiendo…' : 'Pagar con Mercado Pago →'}</button>
              <button className="lb-btn lb-btn--ghost lb-btn--block" style={{ marginTop: 10 }} onClick={() => setStep(1)}>← Volver a datos</button>
              <Footer text={event.footer_text} />
            </div>
          )}

          {(step === 3 || ref) && <ResultStep refId={ref} />}
          </>)}
        </div>
      </div>
      <PointsCatalog eventId={eventId} />
      <style>{`@media (max-width:760px){ .ev-pub-grid{ grid-template-columns:1fr !important } }`}</style>
    </Shell>
  );
}

function ResultStep({ refId }) {
  const [order, setOrder] = useState(null);
  const [tries, setTries] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!refId) return;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/api/public/order/${refId}`);
        const d = await r.json();
        if (r.ok) {
          setOrder(d);
          if (d.status === 'approved' || d.status === 'rejected') { clearInterval(timer.current); }
        }
      } catch {}
      setTries(t => t + 1);
    };
    poll();
    timer.current = setInterval(poll, 3000);
    return () => clearInterval(timer.current);
  }, [refId]);

  if (!order) return <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--ink-600)' }}>Confirmando tu pago…</div>;

  if (order.status === 'rejected') return (
    <div className="lb-fade" style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{ fontSize: 48 }}>✕</div>
      <div className="lb-h" style={{ fontSize: 20, color: 'var(--red-400)' }}>Pago rechazado</div>
      <p style={{ color: 'var(--ink-700)', fontSize: 13, marginTop: 8 }}>No se pudo procesar el pago. Probá de nuevo o con otro medio.</p>
    </div>
  );

  if (order.status !== 'approved') return (
    <div className="lb-fade" style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{ fontSize: 40 }}>⏳</div>
      <div className="lb-h" style={{ fontSize: 18 }}>Pago pendiente</div>
      <p style={{ color: 'var(--ink-700)', fontSize: 13, marginTop: 8 }}>Estamos esperando la confirmación de Mercado Pago. Esta pantalla se actualiza sola{tries > 8 ? '. También te avisamos por email.' : '…'}</p>
    </div>
  );

  const codes = Array.isArray(order.ticket_codes) ? order.ticket_codes : [];
  const trCodes = Array.isArray(order.transport_codes) ? order.transport_codes : [];
  const logo = order.logo_url || '/assets/tukipass-icon.png';
  const isMesa = !!order.mesa_public_code;
  return (
    <div className="lb-fade" style={{ textAlign: 'center' }}>
      <div className="lb-h" style={{ fontSize: 20, marginBottom: 4 }}>¡Listo! 🎉</div>
      <div style={{ color: 'var(--ink-700)', fontSize: 13, marginBottom: 18 }}>{order.event_name}</div>
      {isMesa && (
        <div className="lb-card" style={{ padding: '20px 22px', marginBottom: 18, textAlign: 'left' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, color: '#fff', textTransform: 'uppercase' }}>Mesa {order.mesa_name} · {order.mesa_capacity} personas</div>
          <div style={{ fontSize: 13, color: 'var(--ink-700)', marginTop: 8, lineHeight: 1.5 }}>Entrá a tu link, creá un <b>PIN</b> y generá tus <b>{order.mesa_capacity} entradas</b> (una por persona, cada uno con sus datos). Pasales el link y el PIN a tu grupo. Ahí también ves tu saldo de consumo en tiempo real.</div>
          <a className="lb-btn lb-btn--white lb-btn--block" style={{ marginTop: 14 }} href={`/mesa/${order.mesa_public_code}`} target="_blank" rel="noreferrer">Abrir mi mesa ↗</a>
        </div>
      )}
      {codes.map((c, i) => (
        <div key={c} style={{ marginBottom: 22 }}>
          {codes.length > 1 && <div className="lb-eyebrow" style={{ marginBottom: 8 }}>Entrada {i + 1} de {codes.length}</div>}
          <TicketQR value={c} transportValue={trCodes[i] || null} logo={logo} eventName={order.event_name} date={order.date} lotName={order.lot_name || (isMesa ? `Mesa ${order.mesa_name}` : '')} attendeeName={fullName(order.buyer?.name, order.buyer?.lastname)} filename={`${order.event_name}-${c}.png`} />
          {trCodes[i] && <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 12 }}>Una tarjeta con los dos QR: INGRESO (puerta) y TRASLADO.</div>}
        </div>
      ))}
      <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 8 }}>También te lo enviamos a {order.buyer?.email}</div>
      <Footer />
    </div>
  );
}

function PresaleBlock({ p }) {
  const cell = (n, l) => (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 32, color: '#fff' }}>{String(n).padStart(2, '0')}</div>
      <div style={{ fontSize: 9, letterSpacing: '0.12em', color: 'var(--ink-600)' }}>{l}</div>
    </div>
  );
  return (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{ display: 'inline-block', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 13, letterSpacing: '0.25em', color: 'var(--red-400)', border: '1px solid var(--red-500)', borderRadius: 999, padding: '6px 18px' }}>PRESALE</div>
      <div style={{ color: '#fff', fontWeight: 600, fontSize: 16, marginTop: 18 }}>La venta abre pronto</div>
      <div style={{ color: 'var(--ink-700)', fontSize: 13, marginTop: 4, textTransform: 'capitalize' }}>{fmtPresale(p.at)} hs</div>
      <div style={{ display: 'flex', gap: 18, marginTop: 24, justifyContent: 'center' }}>
        {cell(p.d, 'DÍAS')}{cell(p.h, 'HS')}{cell(p.m, 'MIN')}{cell(p.s, 'SEG')}
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 24 }}>Volvé en ese momento para conseguir tu entrada. ¡No te quedes afuera!</div>
    </div>
  );
}

function Countdown({ date, time }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (!date) return null;
  const target = new Date(`${String(date).slice(0, 10)}T${time && /^\d{2}:\d{2}/.test(time) ? time.slice(0, 5) : '00:00'}`).getTime();
  const diff = target - now;
  if (diff <= 0) return <div style={{ marginTop: 22, color: 'var(--red-400)', fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '0.1em' }}>● EN CURSO</div>;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor(diff / 3600000) % 24;
  const m = Math.floor(diff / 60000) % 60;
  const s = Math.floor(diff / 1000) % 60;
  const cell = (n, l) => (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, color: '#fff' }}>{String(n).padStart(2, '0')}</div>
      <div style={{ fontSize: 9, letterSpacing: '0.12em', color: 'var(--ink-600)' }}>{l}</div>
    </div>
  );
  return <div style={{ display: 'flex', gap: 18, marginTop: 22 }}>{cell(d, 'DÍAS')}{cell(h, 'HS')}{cell(m, 'MIN')}{cell(s, 'SEG')}</div>;
}

function Shell({ children, logo, slug, tema }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '24px', ...(tema || {}) }}>
      <div style={{ maxWidth: 920, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32, gap: 8 }}>
          {slug ? <a href={venueUrl(slug)} className="lb-btn lb-btn--ghost lb-btn--sm" style={{ flex: 'none' }}>← Volver</a> : <span style={{ width: 80 }} />}
          <img src={logo || '/assets/tukipass-icon.png'} alt="" style={{ height: 40 }} onError={e => { e.target.src = '/assets/tukipass-icon.png'; }} />
          {slug ? <a href={`/cuenta/${slug}`} className="lb-btn lb-btn--ghost lb-btn--sm" style={{ flex: 'none' }}>{accesoLabel(slug)}</a> : <span style={{ width: 80 }} />}
        </div>
        {children}
      </div>
    </div>
  );
}

function PointsCatalog({ eventId }) {
  const [data, setData] = useState(null);
  useEffect(() => { fetch(`${API}/api/public/points/${eventId}`).then(r => r.json()).then(setData).catch(() => {}); }, [eventId]);
  if (!data || !data.catalog?.length) return null;
  return (
    <div style={{ marginTop: 48, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 32 }}>
      <div className="lb-display" style={{ fontSize: 26 }}>Canjeá tus <span style={{ color: 'var(--red-400)' }}>puntos.</span></div>
      <p style={{ color: 'var(--ink-700)', fontSize: 14 }}>Sumás puntos en cada compra. Creá tu cuenta y empezá a juntar para canjear por estos premios.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))', gap: 12, marginTop: 16 }}>
        {data.catalog.map(c => (
          <div key={c.id} className="lb-card" style={{ padding: '16px 18px' }}>
            <div style={{ color: 'var(--red-400)', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 22 }}>{c.points_required} pts</div>
            <div style={{ color: '#fff', fontWeight: 600, marginTop: 4 }}>{c.name}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 2 }}>{c.description}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-700)', marginTop: 8 }}>{c.unlimited || c.stock == null ? 'Sin límite' : `${c.stock} disponibles`}</div>
            {data.slug && <a href={`/cuenta/${data.slug}`} className="lb-btn lb-btn--ghost lb-btn--sm lb-btn--block" style={{ marginTop: 12 }}>{accesoLabel(data.slug)}</a>}
          </div>
        ))}
      </div>
    </div>
  );
}

function F({ label, hint, children }) {
  return <div className="lb-field"><div className="lb-label">{label}</div>{children}{hint && <div className="lb-hint">{hint}</div>}</div>;
}
function Footer({ text }) {
  return <div style={{ textAlign: 'center', marginTop: 24, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-600)' }}>{text || 'Acceso reservado · Dress code estricto · +18'}</div>;
}
